import { UserInterface } from "@common/models/user";
import { appConfig } from "@common/config/app.config";
import { BLUETICKS_TOKEN_KEY } from "@common/reusable/common";

// Crisp SDK type declarations
declare global {
  interface Window {
    $crisp: {
      push: (args: any[]) => void;
      get: (method: string, key?: string) => any;
      is: (method: string) => boolean;
    };
  }
}

export interface SubscriptionInfo {
  planName: string;
  duration: string;
  credits: string;
  planAmount: number;
  unitPrice: number;
  quantity: number;
  status: string;
  nextBillingAt: number;
  workspaceName?: string;
}

type FetchLike = (
  url: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string },
) => Promise<{ ok: boolean; status?: number; json: () => Promise<any> }>;

/** Re-mint well inside the backend's 15 min ticket lifetime. */
const ASSISTANT_TICKET_REFRESH_MS = 10 * 60 * 1000;

export class CrispService {
  private assistantTicketTimer: ReturnType<typeof setInterval> | null = null;
  private assistantTicketOff = false;
  private assistantListenersSet = false;
  private assistantTicketRejectedToken: string | null = null;
  private assistantSessionWait = false;

  constructor(private readonly fetchImpl: FetchLike = (url, init) => fetch(url, init)) {}

  /**
   * #139: mint a short-lived backend ticket for THIS Crisp session (POST /crisp-assistant-ticket, as the logged-in user)
   * and push it as session data `bt_ticket`, so the support assistant can read this account. Skipped while
   * impersonating (supportedUserId) and without a login token. A 404 means the feature is off: stop for this page.
   */
  async pushAssistantTicket(): Promise<void> {
    try {
      if (!this.isAvailable || this.assistantTicketOff) return;
      if (new URLSearchParams(window.location?.search ?? "").get("supportedUserId")) return;
      const token = localStorage.getItem(BLUETICKS_TOKEN_KEY);
      if (!token || token === this.assistantTicketRejectedToken) return;
      const crispSessionId = typeof window.$crisp.get === "function" ? window.$crisp.get("session:identifier") : undefined;
      if (typeof crispSessionId !== "string" || !crispSessionId) {
        if (!this.assistantSessionWait) {
          this.assistantSessionWait = true;
          window.$crisp.push(["on", "session:loaded", () => { this.assistantSessionWait = false; void this.pushAssistantTicket(); }]);
        }
        return;
      }
      const res = await this.fetchImpl(`${appConfig.API_ENDPOINT}/crisp-assistant-ticket`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ crispSessionId }),
      });
      if (!res.ok) {
        // 404 = feature off for this page. 401/403 = THIS token is rejected: skip only while it is still the
        // current token (a fresh login mints again), and only stop the timer if no newer login replaced it.
        if (res.status === 404) {
          this.assistantTicketOff = true;
          this.stopAssistantTicketTimer();
        } else if (res.status === 401 || res.status === 403) {
          this.assistantTicketRejectedToken = token;
          if (localStorage.getItem(BLUETICKS_TOKEN_KEY) === token) this.stopAssistantTicketTimer();
        }
        return;
      }
      const body = await res.json();
      if (typeof body?.ticket !== "string" || !body.ticket) return;
      window.$crisp.push(["set", "session:data", [[["bt_ticket", body.ticket]]]]);
    } catch (error) {
      console.warn("Failed to set Crisp assistant ticket:", error);
    }
  }

  private stopAssistantTicketTimer(): void {
    if (this.assistantTicketTimer) {
      clearInterval(this.assistantTicketTimer);
      this.assistantTicketTimer = null;
    }
  }

  private startAssistantTicket(): void {
    void this.pushAssistantTicket();
    if (!this.assistantTicketTimer) {
      this.assistantTicketTimer = setInterval(() => { void this.pushAssistantTicket(); }, ASSISTANT_TICKET_REFRESH_MS);
    }
    if (!this.assistantListenersSet) {
      this.assistantListenersSet = true;
      window.$crisp.push(["on", "chat:opened", () => { void this.pushAssistantTicket(); }]);
    }
  }

  /**
   * Crisp identity verification: the email is pushed ONLY together with the server-computed HMAC signature
   * (GET /crisp-identity, signed with the Crisp identity secret that never reaches the client). Any failure
   * leaves the email unset (anonymous chat) - never an unsigned email, which Crisp rejects once enforcement is on.
   */
  private async pushSignedEmail(): Promise<void> {
    try {
      // The web app keeps its JWT under BLUETICKS_TOKEN_KEY. The extension content script has no such
      // localStorage entry: no token => no call => anonymous chat (fail-closed, never an unsigned email).
      const token = localStorage.getItem(BLUETICKS_TOKEN_KEY);
      if (!token) return;
      const res = await this.fetchImpl(`${appConfig.API_ENDPOINT}/crisp-identity`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const body = await res.json();
      if (typeof body?.email !== "string" || typeof body?.signature !== "string" || !body.email || !body.signature) return;
      window.$crisp.push(["set", "user:email", [body.email, body.signature]]);
    } catch (error) {
      console.warn("Failed to set signed Crisp email:", error);
    }
  }
  
  get isAvailable(): boolean {
    return typeof window === "object" && !!window.$crisp;
  }

  /**
   * Sets user identity data in Crisp chat
   * @param user - User object containing identity information
   */
  setUserIdentity(user?: UserInterface): void {
    if (!this.isAvailable || !user) {
      return;
    }

    try {
      // Set user email if available
      if (user.email) {
        void this.pushSignedEmail();
      }

      // Set user nickname (full name) if available
      const fullName = [user.firstName, user.lastName].filter(x => x).join(" ");
      if (fullName) {
        window.$crisp.push(["set", "user:nickname", [fullName]]);
      }

      // Set user phone if available
      if (user.phone) {
        window.$crisp.push(["set", "user:phone", [user.phone]]);
      }

      // Set custom session data for additional user information
      this.setSessionData(user);

      this.startAssistantTicket();

    } catch (error) {
      console.warn("Failed to set Crisp user identity:", error);
    }
  }

  /**
   * Updates Crisp user with subscription information
   * @param subscriptionInfo - Subscription data to set in Crisp
   */
  updateSubscriptionInfo(subscriptionInfo: SubscriptionInfo): void {
    if (!this.isAvailable) {
      return;
    }

    try {
      const sessionData = [];
      
      // Add subscription information to session data
      sessionData.push(["subscription_plan", subscriptionInfo.planName]);
      sessionData.push(["subscription_duration", subscriptionInfo.duration]);
      sessionData.push(["subscription_credits", subscriptionInfo.credits]);
      sessionData.push(["subscription_amount", `$${subscriptionInfo.planAmount.toFixed(2)}`]);
      sessionData.push(["subscription_unit_price", `$${subscriptionInfo.unitPrice.toFixed(2)}`]);
      sessionData.push(["subscription_quantity", subscriptionInfo.quantity.toString()]);
      sessionData.push(["subscription_status", subscriptionInfo.status]);
      
      // Add next billing date if available
      if (subscriptionInfo.nextBillingAt) {
        const nextBillingDate = new Date(subscriptionInfo.nextBillingAt * 1000).toLocaleDateString();
        sessionData.push(["next_billing_date", nextBillingDate]);
      }
      
      // Add workspace name if available
      if (subscriptionInfo.workspaceName) {
        sessionData.push(["workspace_name", subscriptionInfo.workspaceName]);
      }
      
      // Set the session data
      if (sessionData.length > 0) {
        window.$crisp.push(["set", "session:data", [sessionData]]);
      }

    } catch (error) {
      console.warn("Failed to update Crisp subscription info:", error);
    }
  }

  /**
   * Sets session data with user context
   * @param user - User object containing session data
   */
  private setSessionData(user: UserInterface): void {
    if (!this.isAvailable) {
      return;
    }

    try {
      const sessionData = [];
      
      if (user._id) {
        sessionData.push(["user_id", user._id]);
      }
      
      if (user.analytics_data?.mauticId) {
        sessionData.push(["mautic_id", user.analytics_data.mauticId]);
      }
      
      // Add any additional user context data
      if (user.createdAt) {
        const memberSince = typeof user.createdAt === 'string' 
          ? new Date(user.createdAt).toLocaleDateString()
          : user.createdAt.toString();
        sessionData.push(["member_since", memberSince]);
      }
      
      if (sessionData.length > 0) {
        window.$crisp.push(["set", "session:data", [sessionData]]);
      }

    } catch (error) {
      console.warn("Failed to set Crisp session data:", error);
    }
  }

  /**
   * Resets Crisp session (used on logout)
   */
  resetSession(): void {
    this.stopAssistantTicketTimer();
    this.assistantTicketOff = false; // a later login may mint again
    this.assistantTicketRejectedToken = null;
    if (!this.isAvailable) {
      return;
    }

    try {
      window.$crisp.push(["do", "session:reset"]);
    } catch (error) {
      console.warn("Failed to reset Crisp session:", error);
    }
  }

  /**
   * Opens the Crisp chatbox
   */
  openChat(): void {
    if (!this.isAvailable) {
      return;
    }

    try {
      window.$crisp.push(["do", "chat:open"]);
    } catch (error) {
      console.warn("Failed to open Crisp chat:", error);
    }
  }

  /**
   * Closes the Crisp chatbox
   */
  closeChat(): void {
    if (!this.isAvailable) {
      return;
    }

    try {
      window.$crisp.push(["do", "chat:close"]);
    } catch (error) {
      console.warn("Failed to close Crisp chat:", error);
    }
  }

  /**
   * Sends a message to the chat
   * @param message - Text message to send
   */
  sendMessage(message: string): void {
    if (!this.isAvailable) {
      return;
    }

    try {
      window.$crisp.push(["do", "message:send", ["text", message]]);
    } catch (error) {
      console.warn("Failed to send Crisp message:", error);
    }
  }

  /**
   * Shows a local message in the chatbox (operator message)
   * @param message - Text message to show
   */
  showMessage(message: string): void {
    if (!this.isAvailable) {
      return;
    }

    try {
      window.$crisp.push(["do", "message:show", ["text", message]]);
    } catch (error) {
      console.warn("Failed to show Crisp message:", error);
    }
  }

  /**
   * Adjusts the position of the Crisp chat widget using official API + CSS
   * @param offsetRight - Pixels from the right edge (default: 85)
   */
  adjustPosition(offsetRight: number = 85): void {
    if (!this.isAvailable) {
      return;
    }

    try {
      // Method 1: Use official Crisp API for stable positioning
      // Note: This only allows left/right reversal, not precise pixel positioning
      // We'll use CSS for fine-tuning

      // Add CSS for precise positioning (more reliable than auto-generated classes)
      const existingStyle = document.getElementById('crisp-position-adjustment');
      if (existingStyle) {
        existingStyle.remove();
      }

      const style = document.createElement('style');
      style.id = 'crisp-position-adjustment';
      style.textContent = `
        /* Target the specific crisp-chatbox element - more precise */
        #crisp-chatbox {
          transform: translateX(-${offsetRight}px) !important;
          /* Transition is now global in CSS */
        }
        
        /* Fallback: traditional positioning */
        #crisp-chatbox {
          right: ${offsetRight}px !important;
        }
        
        /* Backup selectors for reliability */
        .crisp-client #crisp-chatbox,
        [id="crisp-chatbox"] {
          transform: translateX(-${offsetRight}px) !important;
        }
      `;
      document.head.appendChild(style);

      console.log(`Crisp chat position adjusted to ${offsetRight}px from right using official API + CSS`);
      
      // Method 2: Direct DOM manipulation as fallback
      const applyDirectStyling = () => {
        // Target the specific crisp-chatbox element
        const crispChatbox = document.getElementById('crisp-chatbox');
        if (crispChatbox) {
          crispChatbox.style.transform = `translateX(-${offsetRight}px)`;
          // Transition is now global in CSS
          console.log(`Applied direct styling to crisp-chatbox element`);
        }
        
        // Fallback: look for crisp elements if chatbox not found
        const fallbackSelectors = ['#crisp-chatbox', '.crisp-client', '[id*="crisp"]'];
        fallbackSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(element => {
            const htmlElement = element as HTMLElement;
            if (htmlElement.id === 'crisp-chatbox' || htmlElement.className.includes('crisp')) {
              htmlElement.style.transform = `translateX(-${offsetRight}px)`;
              // Transition is now global in CSS
              console.log(`Applied fallback styling to:`, htmlElement.id || htmlElement.className);
            }
          });
        });
      };
      
      // Apply direct styling immediately and with delays
      applyDirectStyling();
      setTimeout(applyDirectStyling, 500);
      setTimeout(applyDirectStyling, 1500);
    } catch (error) {
      console.warn("Failed to adjust Crisp position:", error);
    }
  }

  /**
   * Resets the Crisp chat widget position to default (preserves global transition)
   */
  resetPosition(): void {
    const existingStyle = document.getElementById('crisp-position-adjustment');
    if (existingStyle) {
      existingStyle.remove();
      console.log("Crisp chat position reset to default");
    }
    
    // Also reset any direct DOM styling
    const crispChatbox = document.getElementById('crisp-chatbox');
    if (crispChatbox) {
      crispChatbox.style.transform = '';
      // Keep transition - it's global in CSS for smooth animations
    }
  }

  /**
   * Sets up event listeners for Crisp events
   * @param event - Event name to listen for
   * @param callback - Callback function to execute
   */
  addEventListener(event: string, callback: Function): void {
    if (!this.isAvailable) {
      return;
    }

    try {
      window.$crisp.push(["on", event, callback]);
    } catch (error) {
      console.warn(`Failed to add Crisp event listener for ${event}:`, error);
    }
  }

  /**
   * Removes event listener for Crisp events
   * @param event - Event name to stop listening for
   */
  removeEventListener(event: string): void {
    if (!this.isAvailable) {
      return;
    }

    try {
      window.$crisp.push(["off", event]);
    } catch (error) {
      console.warn(`Failed to remove Crisp event listener for ${event}:`, error);
    }
  }

  /**
   * Gets current user email from Crisp
   * @returns User email or null
   */
  getUserEmail(): string | null {
    if (!this.isAvailable) {
      return null;
    }

    try {
      return window.$crisp.get("user:email");
    } catch (error) {
      console.warn("Failed to get user email from Crisp:", error);
      return null;
    }
  }

  /**
   * Checks if chat is currently opened
   * @returns Boolean indicating if chat is open
   */
  isChatOpened(): boolean {
    if (!this.isAvailable) {
      return false;
    }

    try {
      return window.$crisp.is("chat:opened");
    } catch (error) {
      console.warn("Failed to check if Crisp chat is opened:", error);
      return false;
    }
  }
}

// Export singleton instance
export const crispService = new CrispService(); 